import React, { useEffect, useState } from 'react';
import './MarketplaceTemplatePreview.component.scss';
import GanttChart from '../../GanttChartComponent/GanttChart';
import { DataGenerator } from './DataGenerator';
import { GanttChartItem } from '../../GanttChartComponent/models/GanttChartItem.model';
import { useStateSelector } from '../../../store/selectors';
import { useDispatch } from 'react-redux';
import { GetTimelineStages } from '../../../slices/marketplace/timeline/thunks/get-timeline-stages.thunk';
import { USER_ROLES } from '../../../utils/constants';
import Helpers from '../../../utils/helper';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import { TimelineStage } from '../../../slices/marketplace/timeline/models/timeline-stage.model';
import { TimelineEventStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { EngagementBuyerActionState } from '../../../slices/marketplace/timeline/models/engagement-buyer-action-state.model';

interface MarketplaceTemplatePreviewProps {
    events?: TimelineEvent[];
    buyerActionState?: EngagementBuyerActionState[];
    startDate?: Date;
    displayCurrentDayIndicator: boolean;
    onEventClick(item: GanttChartItem): void;
    selectedEventId?: string;
    isEventClickable?: (eventId: string) => boolean;
    currentDate?: Date;
}

export const MarketplaceTemplatePreviewComponent = (
    props: MarketplaceTemplatePreviewProps
) => {
    const dispatch = useDispatch() as any;
    const config = {
        header: {
            month: {
                dateFormat: 'MMMM  YYYY',
                style: {
                    background: 'linear-gradient( grey, black)',
                    textShadow: '0.5px 0.5px black',
                    fontSize: 12,
                },
            },
            dayOfWeek: {
                style: {
                    background: 'linear-gradient( orange, grey)',
                    fontSize: 9,
                },
            },
            dayTime: {
                style: {
                    background: 'linear-gradient( grey, black)',
                    fontSize: 9,
                    color: 'orange',
                },
                selectedStyle: {
                    background: 'linear-gradient( #d011dd ,#d011dd)',
                    fontWeight: 'bold',
                    color: 'white',
                },
            },
        },
        taskList: {
            title: {
                label: 'Task Todo',
                style: {
                    background: 'linear-gradient( grey, black)',
                },
            },
            task: {
                style: {
                    backgroundColor: 'grey',
                    color: 'white',
                },
            },
            verticalSeparator: {
                style: {
                    backgroundColor: '#fbf9f9',
                },
                grip: {
                    style: {
                        backgroundColor: 'red',
                    },
                },
            },
        },
        dataViewPort: {
            rows: {
                style: {
                    backgroundColor: 'white',
                },
            },
            task: {
                showLabel: true,
                style: {
                    borderRadius: '0px',
                    boxShadow: 'none',
                },
            },
        },
    };
    const [generator] = useState(new DataGenerator());
    const [data, setData] = useState<GanttChartItem[]>([]);
    const [targetEventId, setTargetEventId] = useState('');
    const [state, setState] = useState<{
        itemheight: number;
        daysWidth: number;
        startDate: Date;
        endDate: Date;
    }>();
    const summary = useStateSelector((s) => s.userSummary.summary);
    const [isLoading, setIsLoading] = useState(true);

    const getMarketplaceAuthorizedParty = (): TimelineParty => {
        if (summary.isMarketplaceBuyer) {
            return TimelineParty.Buyer;
        }
        if (summary?.roleId === USER_ROLES.ADMIN) {
            return TimelineParty.VetValue;
        }
        return TimelineParty.Seller;
    };

    const getEventIdToFocus = (events: TimelineEvent[]): string => {
        const earliestInProgressEvent = events.find(
            (f) =>
                f.status === TimelineEventStatus.InProgress ||
                f.status === TimelineEventStatus.Late
        );

        const latesCompletedEvent = events
            .slice()
            .filter((f) => f.status === TimelineEventStatus.Completed)
            .sort((a, b) => {
                const actualDayEndA = a.dayEnd;
                const actualDayEndB = b.dayEnd;
                if (actualDayEndA === actualDayEndB) {
                    return a.name.localeCompare(b.name);
                }
                return actualDayEndB - actualDayEndA;
            })
            .find((f) => f);

        const eventToFocus =
            earliestInProgressEvent?.id ?? latesCompletedEvent?.id;
        return eventToFocus ?? '';
    };

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            let events: TimelineEvent[] = props.events;
            const result = await dispatch(GetTimelineStages());
            const stages: TimelineStage[] = result.payload;

            const lastEventDay = events.reduce(
                (max, event) => Math.max(max, event.dayEnd),
                0
            );

            const authorizedParty = getMarketplaceAuthorizedParty();
            const projectionBase = props.startDate
                ? new Date(props.startDate)
                : new Date();
            const chartOrigin = new Date(projectionBase);
            chartOrigin.setHours(0, 0, 0, 0);
            const data = generator.generateData(
                stages,
                events,
                authorizedParty,
                props.buyerActionState ?? [],
                props.isEventClickable,
                projectionBase
            );
            setData(data);
            const eventIdToFocus = getEventIdToFocus(events);
            setTargetEventId(eventIdToFocus);

            const endDate = new Date();
            endDate.setDate(endDate.getDate() + lastEventDay);
            setState({
                itemheight: 30,
                daysWidth: 0,
                startDate: chartOrigin,
                endDate: endDate,
            });
            setIsLoading(false);
        };

        loadData().then();
    }, [props.events]);

    return (
        <div className="associate-members-table mt-3 gantt-chart-container">
            <div className="marketplace-template-preview gantt-chart-container">
                <div className="without_title_content gantt-chart-container">
                    {isLoading ? (
                        Helpers.renderPageLoader()
                    ) : data?.length > 0 ? (
                        <GanttChart
                            config={config}
                            onTaskClick={props.onEventClick}
                            data={data}
                            itemheight={state.itemheight}
                            startDate={state.startDate}
                            endDate={state.endDate}
                            displayCurrentDayIndicator={
                                props.displayCurrentDayIndicator
                            }
                            isTaskClickable={props.isEventClickable}
                            focusEventId={targetEventId}
                            currentDate={props.currentDate}
                        />
                    ) : (
                        <h2
                            className="no-page-access-alert"
                            style={{ padding: '0 21px 30px' }}>
                            No events available to display
                        </h2>
                    )}
                </div>
            </div>
        </div>
    );
};
